import { Trans, useTranslation } from "react-i18next";
import AccountHeader from "../../AccountHeader";
import { Alert, Container, Stack, Typography, type ChipProps } from "@mui/material";
import { type ReactElement, useMemo } from "react";
import tKey from "../../../Common/Utils/tKey";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { useSearchParams } from "react-router";
import useStoreFlagError from "../../../Common/_hooks/Store/useStoreFlagError";
import StoreSetupCard from "./StoreSetupCard";
import StoreShippingTablesEmbed from "./StoreShippingTablesEmbed";
import StoreConnectionStepper from "./StoreConnectionStepper";
import useStoreData from "../../../Common/_hooks/Store/useStoreData";

export default function SquarespaceConnect() {
  const [t] = useTranslation("account-v2");
  const [tR] = useTranslation("routes");

  const [searchParams] = useSearchParams();

  const storeSlug = "squarespace";
  const setupUrl = tR("/account/stores/connect/{{store}}", { store: storeSlug });

  const store = useStoreData(storeSlug);

  const isFlagged = useMemo(() => store?.get("flagged"), [store]);
  const isMissingScope = !!store?.get("is_missing_scope");
  const flagErrorMessage = useStoreFlagError(storeSlug);
  const isConnected = !!store && !isFlagged && !isMissingScope;
  const connectUrl = `${setupUrl}?connect=1`;
  const startConnectUrl = tR("/d/{{store}}/connect", { store: storeSlug }) + "?connect=1";

  const connectErrorMessage = useMemo(() => {
    const error = searchParams.get("error");
    if (!error) return null;

    switch (error) {
      case "access_denied":
        return t("It looks like you canceled the Squarespace authorization step. Follow the steps below again, choose the correct website, and click Allow to finish connecting your store.");
      case "already-exists":
        return t("This Squarespace store is already linked to another Art of Where account. Please remove it from the other account first.");
      default:
        return t("An error occurred while connecting to your store. Please try again.");
    }
  }, [searchParams, t]);

  const connectionStatus = useMemo((): { label: string, color: ChipProps["color"], icon: ReactElement } => {
    if (isConnected) {
      return {
        label: t("Connected"),
        color: "success",
        icon: <CheckCircleIcon color="success" />,
      };
    }

    if (isFlagged) {
      return {
        label: t("Needs attention"),
        color: "error",
        icon: <ErrorOutlineRoundedIcon color="error" />,
      };
    }

    if (isMissingScope) {
      return {
        label: t("Needs attention"),
        color: "warning",
        icon: <ErrorOutlineRoundedIcon color="warning" />,
      };
    }

    return {
      label: t("Not connected"),
      color: "default",
      icon: <RadioButtonUncheckedRoundedIcon color="action" />,
    };
  }, [isConnected, isFlagged, isMissingScope, t]);

  return <div>
    <AccountHeader
      title={t("Set up your {{store}} store", { store: "Squarespace" })}
    />

    <Container
      maxWidth="md"
      disableGutters
      style={{ margin: 0 }}
    >
      <Stack spacing={4}>
        <Typography variant="body1">
          {t("Connect your Squarespace store first, then follow the shipping steps below to finish setting it up.")}
        </Typography>

        <StoreSetupCard
          icon={connectionStatus.icon}
          title={t("Connection")}
          status={connectionStatus.label}
          statusColor={connectionStatus.color}
        >
          <StoreConnectionStepper
            t={t}
            hasStoreStepLabel={t("Do you already have a Squarespace website?")}
            noStoreTitle={t("Create your Squarespace website")}
            noStoreDescription={<Typography variant="body1">
              <Trans
                t={t}
                i18nKey={tKey("Go to <a>Squarespace</a> and create your website now. Make sure Commerce is enabled before you continue.")}
                components={{
                  a: <a href="https://www.squarespace.com/" target="_blank" />
                }}
              />
            </Typography>}
            connectStepLabel={t("Connect your Squarespace website to Art of Where")}
            connectSteps={[
              t("Click the button below to start the Squarespace authorization flow."),
              t("Sign in to Squarespace if you are prompted to do so."),
              t("Select the website you want to connect to Art of Where."),
              t("Review the requested permissions and click Allow to finish connecting your store."),
            ]}
            connectButtonLabel={t("Connect to Squarespace")}
            connectUrl={connectUrl}
            startConnectRequested={searchParams.get("connect") === "1"}
            startConnectUrl={startConnectUrl}
            connectInfo={<Alert severity="info">
              {t("If you manage multiple Squarespace websites, choose the one you want to connect when Squarespace asks you to select a site.")}
            </Alert>}
            connectErrorMessage={connectErrorMessage}
            isConnected={isConnected}
            isFlagged={!!isFlagged}
            isMissingScope={isMissingScope}
            flagErrorMessage={flagErrorMessage}
            connectedContent={<Stack spacing={1}>
              <Typography variant="body1">
                {t("Your Squarespace website is connected and ready for product publishing and order syncing.")}
              </Typography>
              <Typography variant="body2">
                {t("When you publish products from Art of Where, you will choose the Squarespace store page where they should appear.")}
              </Typography>
            </Stack>}
          />
        </StoreSetupCard>

        <StoreSetupCard
          icon={<LocalShippingOutlinedIcon color="secondary" />}
          title={t("Shipping")}
          status={isConnected ? t("Manual step") : t("After connection")}
          statusColor={isConnected ? "warning" : "default"}
        >
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="body1">
              {t("Use Squarespace weight-based shipping for Canada, the US, and Rest of World.")}
            </Typography>

            <Alert severity="info">
              {t("For more precision, split Canada by province and the US by state or zone. This takes longer to configure.")}
            </Alert>

            <Stack spacing={1} sx={{ width: "100%" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t("Shipping options")}
              </Typography>
              <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("<strong>Open Settings > Selling > Shipping</strong> in Squarespace.")}
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("Under <strong>Shipping options</strong>, click <strong>Add shipping option</strong> to create your rates.")}
                    components={{ strong: <strong /> }}
                  />
                </li>
              </ol>
            </Stack>

            <Stack spacing={1} sx={{ width: "100%" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t("Rates")}
              </Typography>
              <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
                <li>{t("Create weight-based shipping tables for Canada, the US, and Rest of World.")}</li>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("The rates below are the Canada and US <strong>Standard</strong> rates.")}
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("For <strong>Rest of World</strong>, use <strong>International Tracked</strong>. For DHL-only countries, add a country-specific option with <strong>DHL Express Worldwide</strong>.")}
                    components={{ strong: <strong /> }}
                  />
                </li>
              </ol>
            </Stack>

            <StoreShippingTablesEmbed
              defaultView="weight"
              allowedViews={["weight"]}
              weightTableDisplay="orders_up_to"
              typeOptionsByCountry={{
                ca: [{ label: t("Standard"), value: "canada-expedited-parcel" }],
                us: [{ label: t("Standard"), value: "us-standard", zoneSourceValue: "usps-priority-mail" }],
                in: [
                  { label: t("International Tracked"), value: "intl-tracked" },
                  { label: t("DHL Express Worldwide"), value: "dhl-express-worldwide" },
                ],
              }}
            />
          </Stack>
        </StoreSetupCard>
      </Stack>
    </Container>
  </div>;
}
